import { CancelFunctions } from 'components/common/types';
import { DirectSalesLine } from 'components/common/types/DirectSalesCampaign.types';
import { ProductTemplate, ProductTemplateResponseLine } from 'components/common/types/DirectSalesProductTemplate.types';
import { fetchDirectSalesProduct } from 'modules/api/directSalesProductTemplate';
import { DEFAULT_PRODUCT_TEMPLATE } from './consts';

type DealLineProductMapping = {
  dealLineId: string;
  productTemplateId: string;
  productLineReference: number;
};

export const getAllDealLineProductTemplates = async (
  lines: Partial<DirectSalesLine>[],
  marketId: string,
  cancelFunctions: CancelFunctions,
  errorCallback: VoidFunction,
): Promise<ProductTemplate[]> => {
  const dealLineProductMappings = lines.reduce<DealLineProductMapping[]>((acc, line) => {
    const productTemplateId = line.productTemplate?.id;
    const { productLineReference, lineId } = line;

    if (productTemplateId && productLineReference !== undefined && lineId) {
      acc.push({ dealLineId: lineId, productTemplateId, productLineReference });
    }
    return acc;
  }, []);

  const uniqueProductTemplateIds = [
    ...new Set(dealLineProductMappings.map(({ productTemplateId }) => productTemplateId).filter((id) => id != null)),
  ];

  const fetchedProducts = new Map();

  await Promise.all(
    uniqueProductTemplateIds.map(async (productTemplateId) => {
      if (!productTemplateId) return;

      const response = await fetchDirectSalesProduct(marketId, cancelFunctions, productTemplateId);
      fetchedProducts.set(productTemplateId, response);
    }),
  ).catch(() => errorCallback());

  const productTemplatesWithDealLines = dealLineProductMappings.map(
    ({ dealLineId, productTemplateId, productLineReference }) => {
      if (!productTemplateId) {
        return DEFAULT_PRODUCT_TEMPLATE;
      }

      const productResponse = fetchedProducts.get(productTemplateId);

      if (!productResponse) {
        return DEFAULT_PRODUCT_TEMPLATE;
      }

      const specificLine = productResponse.lines?.find(
        (line: ProductTemplateResponseLine) => line.lineReference === productLineReference,
      );
      const rules = specificLine?.rules ?? productResponse.rules ?? [];

      return {
        dealLineId,
        productTemplate: {
          id: productResponse.id,
          name: productResponse.name,
        },
        folder: productResponse.folder,
        status: productResponse.status,
        rules,
        productLineReference,
      };
    },
  );

  return productTemplatesWithDealLines?.filter(({ productTemplate }) => !!productTemplate?.id) ?? [];
};
